import {
  useLoaderData,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { client } from '~/client';
import { CreateJobForm } from '~/components/job/CreateJobForm';
import { graphql } from '~/gql';

const CreateJobFormDocument = graphql(`
  query CreateJobForm {
    integrations
    attributes(entityType: "Site") {
      id
      type
      category
      name
      value
      condition
    }
    jobNameOptions
    jobTemplates(status: Active) {
      ...JobTemplateFields
    }
    locations(status: Active) {
      __typename
      id
      name
      ... on Asset {
        deployment {
          id
          displayLocation
          defaultSourceId
        }
      }
    }
    contacts(status: Active) {
      ...ContactFields
    }
  }
`);

export async function loader() {
  const { data, error } = await client
    .query(CreateJobFormDocument, {}, { requestPolicy: 'cache-first' })
    .toPromise();

  if (error || !data) {
    throw new Response(error?.message, { status: 500 });
  }

  return data;
}

export default function CreateJobRoute() {
  const data = useLoaderData() as Awaited<ReturnType<typeof loader>>;
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const urlSiteId = searchParams.get('siteId');
  const urlDate = searchParams.get('date');

  // Force remount when URL parameters change
  const key = `${urlSiteId || ''}:${urlDate || ''}`;
  return (
    <CreateJobForm
      key={key}
      data={data}
      onClose={() => {
        const path = location.pathname;
        // Prefer back navigation when we have a prior entry in the app history
        // location.key === 'default' indicates the initial entry
        if (location.key !== 'default') {
          navigate(-1);
          return;
        }

        // Safe fallback if no history to go back to — route by context
        // Activity context
        if (path.startsWith('/activity')) {
          navigate('/activity');
          return;
        }

        // Monthly calendar nested context
        if (path.includes('/jobs/calendar/')) {
          navigate('..');
          return;
        }

        // Global calendar context
        if (path.startsWith('/jobs/planner')) {
          navigate('/jobs/planner');
          return;
        }

        // Specific jobs view context (e.g., /jobs/list/new)
        if (params.view) {
          navigate(`/jobs/${params.view}`);
          return;
        }

        // Final fallback
        navigate('/jobs');
      }}
    />
  );
}
